/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

/**
 * Provider-aware assistant client — the ONE seam for the backend.
 *
 * The rest of the UI never knows which model answers. It calls `streamMessage()`
 * and consumes stream events. The swap between the local mock and the real backend
 * (SSE) happens ONLY in this file.
 *
 * Provider selection (Vecros default vs. BYOK Claude/ChatGPT) is a BACKEND concern:
 * the browser always talks to our backend, which routes to the configured provider.
 * The raw API key is NEVER held here.
 */

import { PROVIDERS } from "./types";
import { scriptReply } from "./mockData";
import { getAuthToken } from "../../../utils/authToken";
import { resolveApiBase } from "../../../lib/apiBase";

/**
 * Base URL of the assistant inference API (the vecros-api FastAPI backend).
 * Resolved at runtime (see src/lib/apiBase.js) so a build that baked a localhost
 * URL doesn't make the deployed site call localhost:
 *  - localhost  → the local dev runner on :8787
 *  - deployed   → the baked non-localhost URL, else dev-api.vecros.com.
 */
export const ASSISTANT_API_BASE = resolveApiBase();

/**
 * Which Vecros product this assistant is embedded in — the backend uses it to pick
 * the system prompt, skills and MCP connectors.
 */
export const ASSISTANT_SERVICE = "developer";

const PROVIDER_STORAGE_KEY = "vcs_ai_provider";

/**
 * Which provider the tenant has selected. Only the *choice* lives client-side.
 * @returns {{ provider: string }}
 */
export const getProviderConfig = () => {
  try {
    const stored = window.localStorage.getItem(PROVIDER_STORAGE_KEY);
    if (stored && Object.values(PROVIDERS).includes(stored)) return { provider: stored };
  } catch (_) {
    /* localStorage unavailable — fall through to default */
  }
  return { provider: PROVIDERS.VECROS };
};

/** Persist the tenant's provider choice. */
export const setProviderConfig = (provider) => {
  try {
    window.localStorage.setItem(PROVIDER_STORAGE_KEY, provider);
  } catch (_) {
    /* no-op */
  }
};

/**
 * Send a BYOK API key to the backend to be stored encrypted and used server-side.
 * The raw key is NEVER persisted in the browser. Stubbed until the endpoint exists.
 */
export async function saveProviderKey(provider, apiKey) {
  // eslint-disable-next-line no-console
  console.warn("[assistant] saveProviderKey stub — backend endpoint not wired yet", { provider });
  return Promise.resolve({ ok: true });
}

/** Flip to true to force the local mock instead of the real backend. */
export const USE_MOCK = false;

/**
 * Stream an assistant reply as an async iterable of StreamEvent.
 * @param {{ message: string, assistantChatId?: string, context?: any[], signal?: AbortSignal }} req
 * @returns {AsyncGenerator<import("./types").StreamEvent>}
 */
export async function* streamMessage(req) {
  const { provider } = getProviderConfig();
  if (USE_MOCK) {
    yield* streamFromMock(req);
    return;
  }
  yield* streamFromBackend({ ...req, provider });
}

/* ------------------------------------------------------------------ mock ---- */

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener("abort", () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      });
    }
  });

async function* streamFromMock({ message, assistantChatId, signal }) {
  if (!assistantChatId) {
    yield { type: "chat", assistantChatId: `mock-${Math.floor(performance.now())}` };
  }
  await wait(300, signal);
  for (const event of scriptReply(message)) {
    await wait(event.type === "token" ? 90 : 260, signal);
    yield event;
  }
}

/* --------------------------------------------------------------- backend ---- */

/**
 * Real backend stream. SSE from the streaming endpoint: parse each `data:` frame as a
 * StreamEvent and yield it. Send `provider` in the body so the backend routes to the
 * right model.
 */
async function* streamFromBackend(req) {
  const token = typeof getAuthToken === "function" ? getAuthToken() : null;
  let res;
  try {
    res = await fetch(`${ASSISTANT_API_BASE}/assistant/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: req.message,
        service: ASSISTANT_SERVICE,
        assistant_chat_id: req.assistantChatId || null,
        provider: req.provider || null,
        context: req.context || null,
        account_context: req.accountContext || null,
        routes: req.routes || null,
      }),
      signal: req.signal,
    });
  } catch (err) {
    if (err && err.name === "AbortError") return;
    yield { type: "error", code: "NETWORK", message: "Couldn't reach the assistant service." };
    return;
  }

  if (!res.ok || !res.body) {
    yield { type: "error", code: `HTTP_${res.status}`, message: "The assistant request failed." };
    return;
  }

  // Parse the Server-Sent Events stream: one JSON StreamEvent per `data:` frame.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const line = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        try {
          yield JSON.parse(data);
        } catch (_) {
          /* skip malformed frame */
        }
      }
    }
  } catch (err) {
    if (!err || err.name !== "AbortError") {
      yield { type: "error", code: "STREAM", message: "The connection was interrupted." };
    }
  }
}
